// "Add from bank" — the question bank, read back into a paper.
//
// One subject at a time, because a paper is set in one subject and a picker
// that offers Physics to a Mathematics paper is noise. Teacher-only: a bank
// question carries its answer key and marking scheme, which is what lets it
// arrive in the paper with its TEAM already written.
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { BANK_PAGE } from '@cwy/config/contracts';
import { apiGet } from '@/lib/api';
import type { BankQuestionDTO, QuestionBankPage } from '@/types';
import { MathText } from '@/components/exam/MathText';
import { QUESTION_TYPES, messageFor } from './editorItems';

const TYPE_LABEL: Record<BankQuestionDTO['type'], string> = {
  MCQ: 'MCQ',
  SHORT_DESC: 'Short',
  MEDIUM_DESC: 'Medium',
  LONG_DESC: 'Long',
};

export function QuestionBankPicker({
  subjectId,
  subjectName,
  inPaper,
  onAdd,
  onClose,
}: {
  subjectId: string;
  subjectName: string;
  /** Bank ids already on this paper, so the same question is not added twice. */
  inPaper: Set<string>;
  onAdd: (question: BankQuestionDTO) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [type, setType] = useState('');

  const bank = useQuery({
    queryKey: ['question-bank', subjectId, q, chapterId, type],
    queryFn: () => {
      const params = new URLSearchParams({ subjectId });
      if (q.trim()) params.set('q', q.trim());
      if (chapterId) params.set('chapterId', chapterId);
      if (type) params.set('type', type);
      return apiGet<QuestionBankPage>(`/teacher/question-bank?${params}`);
    },
    // Keep the last list up while the next loads, so typing does not flash
    // the picker empty on every key.
    placeholderData: keepPreviousData,
  });

  const data = bank.data;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/25 p-4 sm:p-10"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label={`Question bank — ${subjectName}`}
        className="flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-line bg-card shadow-pop"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div>
            <p className="text-sm font-bold text-ink">Add from the question bank</p>
            <p className="text-xs text-slate-500">
              {subjectName} · each question arrives with its TEAM already written
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close the bank"
            className="text-lg leading-none text-slate-500"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-line bg-paper-sunk px-4 py-2.5">
          <input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Search question text, topic or sub-topic"
            className="min-w-[200px] flex-1 rounded-lg border border-line bg-card px-2.5 py-1.5 text-sm outline-none focus:border-forest"
          />
          <select
            value={chapterId}
            onChange={(event) => setChapterId(event.target.value)}
            className="rounded-lg border border-line bg-card px-2 py-1.5 text-xs outline-none focus:border-forest"
          >
            <option value="">All topics</option>
            {data?.chapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
            className="rounded-lg border border-line bg-card px-2 py-1.5 text-xs outline-none focus:border-forest"
          >
            <option value="">All types</option>
            {QUESTION_TYPES.map((questionType) => (
              <option key={questionType.value} value={questionType.value}>
                {questionType.label}
              </option>
            ))}
          </select>
        </div>

        <ul className="flex-1 overflow-y-auto">
          {bank.isLoading && <li className="m-4 h-24 animate-pulse rounded-xl bg-slate-100" />}
          {bank.isError && (
            <li className="px-4 py-10 text-center text-sm text-red-700">
              {messageFor(bank.error)}
            </li>
          )}
          {data?.results.length === 0 && (
            <li className="px-4 py-10 text-center text-sm text-slate-500">
              Nothing in the bank matches that. Try fewer words, or all topics.
            </li>
          )}
          {data?.results.map((b) => {
            const added = inPaper.has(b.id);
            return (
              <li
                key={b.id}
                className="flex items-start gap-3 border-b border-line px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-1.5 text-fs-10 font-bold">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                      {TYPE_LABEL[b.type]}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                      {b.maxMarks} {b.maxMarks === 1 ? 'mark' : 'marks'}
                    </span>
                    {b.authored && (
                      <span className="rounded-full bg-forest/10 px-2 py-0.5 text-forest">
                        From your school
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-ink">
                    <MathText>{b.text}</MathText>
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {b.subtopicName} · {b.chapterName}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onAdd(b)}
                  disabled={added}
                  className="flex-none rounded-lg border border-forest px-3 py-1.5 text-xs font-semibold text-forest hover:bg-forest/5 disabled:border-line disabled:text-slate-400"
                >
                  {added ? 'In this paper' : 'Add'}
                </button>
              </li>
            );
          })}
        </ul>

        {data && data.total > BANK_PAGE && (
          <p className="border-t border-line px-4 py-2 text-fs-11 text-slate-500">
            Showing {BANK_PAGE} of {data.total}. Narrow the search to see the rest.
          </p>
        )}
      </div>
    </div>
  );
}
